import {
  getIntlUnit
} from "./chunk-PBJFE4G3.jsx";
import {
  getCurrentDate
} from "./chunk-5VL5IYXF.jsx";
import {
  roundOffTo
} from "./chunk-KTO2XXJZ.jsx";
import {
  __name,
  __spreadValues
} from "./chunk-DVUTYNGK.jsx";

// src/utils/time/time.ts
function convertMillisecondsToHours(milliseconds, enableRoundOff = true) {
  if (!milliseconds)
    return {
      hour: null,
      minute: null
    };
  const hour = Math.floor(milliseconds / 1e3 / 60 / 60);
  let minute = Math.floor((milliseconds / 1e3 / 60 / 60 - hour) * 60);
  if (enableRoundOff) {
    minute = roundOffTo(minute, 5);
  }
  return {
    hour,
    minute
  };
}
__name(convertMillisecondsToHours, "convertMillisecondsToHours");
var formatPartsToDuration = /* @__PURE__ */ __name((arr) => {
  if (arr.length === 3) {
    const [, value, unit] = arr || [];
    return `${value == null ? void 0 : value.value}${unit == null ? void 0 : unit.value}`;
  } else {
    const [value, unit] = arr || [];
    return `${value == null ? void 0 : value.value}${unit == null ? void 0 : unit.value}`;
  }
}, "formatPartsToDuration");
var formatDurationToString = /* @__PURE__ */ __name(({
  hour,
  minute,
  lang,
  style = "long"
}) => {
  let res = "";
  if (hour) {
    const hourParts = localisedRelativeTimeFormat({
      locale: lang,
      unit: "hour",
      value: hour,
      formatToParts: true,
      style
    });
    res += typeof hourParts === "string" ? hourParts : formatPartsToDuration(hourParts);
  }
  if (minute) {
    const minuteParts = localisedRelativeTimeFormat({
      locale: lang,
      unit: "minute",
      value: minute,
      formatToParts: true,
      style
    });
    res += res ? " " : "";
    res += typeof minuteParts === "string" ? minuteParts : formatPartsToDuration(minuteParts);
  }
  return res;
}, "formatDurationToString");
var getDuration = /* @__PURE__ */ __name(({
  minDuration,
  maxDuration,
  lang = "en",
  style = "long"
}) => {
  if (minDuration !== maxDuration) {
    const { hour: minHour, minute: minMinute } = convertMillisecondsToHours(minDuration);
    const { hour: maxHour, minute: maxMinute } = convertMillisecondsToHours(maxDuration);
    return `${formatDurationToString({
      hour: minHour,
      minute: minMinute,
      lang,
      style
    })} - ${formatDurationToString({
      hour: maxHour,
      minute: maxMinute,
      lang,
      style
    })}`;
  } else {
    const { hour, minute } = convertMillisecondsToHours(maxDuration, false);
    return formatDurationToString({ hour, minute, lang, style });
  }
}, "getDuration");
var getDurationInDays = /* @__PURE__ */ __name((duration) => Math.round(duration / 1440), "getDurationInDays");
var getDurationInHours = /* @__PURE__ */ __name((duration) => Math.round(duration / 60), "getDurationInHours");
var getDurationInHmNotation = /* @__PURE__ */ __name(({
  lang,
  durationInMinutes,
  unitDisplay = "long"
}) => {
  let hours = Math.floor(durationInMinutes / 60);
  let minutes = durationInMinutes % 60;
  if (durationInMinutes <= 90) {
    hours = 0;
    minutes = durationInMinutes;
  }
  let durationString = "";
  switch (true) {
    case hours === 0:
      durationString = getIntlUnit({
        lang,
        number: minutes,
        options: {
          unit: "minute",
          unitDisplay
        }
      });
      break;
    case minutes === 0:
      durationString = getIntlUnit({
        lang,
        number: hours,
        options: {
          unit: "hour",
          unitDisplay
        }
      });
      break;
    default:
      const intlHour = getIntlUnit({
        lang,
        number: hours,
        options: {
          unit: "hour",
          unitDisplay
        }
      });
      const intlMin = getIntlUnit({
        lang,
        number: minutes,
        options: {
          unit: "minute",
          unitDisplay
        }
      });
      durationString = `${intlHour} ${intlMin}`;
      break;
  }
  return durationString;
}, "getDurationInHmNotation");
var localisedRelativeTimeFormat = /* @__PURE__ */ __name(({
  locale,
  value,
  unit,
  formatToParts = false,
  localeMatcher = "lookup",
  numeric = "auto",
  style = "short"
}) => {
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, {
      localeMatcher,
      numeric,
      style
    });
    return formatToParts ? rtf.formatToParts(value, unit) || [] : rtf.format(value, unit);
  } catch (e) {
    return "";
  }
}, "localisedRelativeTimeFormat");
var getLocalizedTime = /* @__PURE__ */ __name(({
  lang,
  formattedTime,
  trimTrailingZeros = false,
  isLowerCase = false
}) => {
  try {
    if (!formattedTime) return formattedTime;
    const [hours, minutes] = formattedTime.split(":").map(Number);
    const date = /* @__PURE__ */ new Date();
    date.setHours(hours);
    date.setMinutes(minutes);
    const showMinutes = minutes !== 0 && !trimTrailingZeros;
    const options = __spreadValues({
      hour: "numeric"
    }, showMinutes && { minute: "numeric" });
    const time = new Intl.DateTimeFormat(
      lang,
      options
    ).format(date);
    if (isLowerCase) return time.split(" ").join("").toLowerCase();
    return time;
  } catch (error) {
    return formattedTime;
  }
}, "getLocalizedTime");
function getIntlTime({
  time,
  lang,
  options = {}
}) {
  try {
    if (!time) return time;
    const now = /* @__PURE__ */ new Date(`${getCurrentDate()}T${time}`);
    const format = new Intl.DateTimeFormat(lang, __spreadValues({
      hour: "numeric",
      minute: "numeric",
      hour12: lang === "en" ? true : false
    }, options)).format(now);
    return format;
  } catch (error) {
    return time;
  }
}
__name(getIntlTime, "getIntlTime");

export {
  convertMillisecondsToHours,
  formatDurationToString,
  getDuration,
  getDurationInDays,
  getDurationInHours,
  getDurationInHmNotation,
  localisedRelativeTimeFormat,
  getLocalizedTime,
  getIntlTime
};
